export interface NtpInfo {
  epoch?: number;
  utc?: string;
  source?: string;
}

export type EventName =
  | 'note' | 'NOTE'
  | 'link'
  | 'task'
  | 'start' | 'stop'
  | 'pause' | 'resume'
  | 'contract-start' | 'contract-end'
  | 'subtract'
  | 'correct'
  | 'item-status' | 'item-state'
  | 'task-time' | 'block' | 'unspecified-time'
  | 'revise'
  | 'ping'
  | string;

export interface WorklogEvent {
  event: EventName;
  ntp?: NtpInfo;
  local?: string;
  contract?: string;
  category?: string;
  task?: string;
  active_task?: string;
  note?: string;
  url?: string;
  reason?: string;
  duration_s?: number;
  pause_duration_s?: number;
  elapsed_s?: number;
  seconds?: number;
  subtracted_s?: number;
  item?: string;
  new_status?: string;
  prev_status?: string;
  action?: string;
  label?: string;
  to_epoch?: number;
  target_epoch?: number;
  original_note?: string;
  project?: string;
  /** revise: before/after pair with optional title and per-segment tags. */
  title?: string;
  before?: string;
  after?: string;
  title_tags?: string[];
  before_tags?: string[];
  after_tags?: string[];
  [extra: string]: unknown;
}

export type ItemStatus = 'open' | 'active' | 'blocked' | 'review' | 'partial' | 'done' | 'dropped';

export interface Item {
  id: string;
  label?: string;
  status?: ItemStatus | string;
  block_reason?: string;
  estimate_minutes?: number;
  contract?: string | null;
  priority?: number;
  [extra: string]: unknown;
}

export type RelationKind =
  | 'prereq_of' | 'depends_on'
  | 'blocks' | 'blocked_by'
  | 'adjacent' | 'duplicates' | 'references';

export type DetailKind =
  | 'feature' | 'challenge' | 'filepath'
  | 'residence' | 'reference' | 'note' | 'followup';

export interface TodoRelation {
  kind: RelationKind;
  from_id: number;
  to_id: number;
}

export interface TodoDetail {
  kind: DetailKind;
  value: string;
  ord: number;
}

export interface Todo {
  id: number | string;
  name?: string;
  details?: string;
  resolved?: boolean;
  created_at?: string;
  resolved_at?: string | null;
  category?: string | null;
  // V2: tags is a real JSON array from the join table.
  // V1 published a JSON string; parsers handle both for one migration cycle.
  tags?: string[] | string | null;
  hard_deadline?: string | null;
  soft_deadline?: string | null;
  commitment?: string | null;
  parent_id?: number | null;
  // V2 additions
  relations?: TodoRelation[];
  details_struct?: TodoDetail[];
  [extra: string]: unknown;
}

export interface WorklogFetch {
  events: WorklogEvent[];
  fetched_at?: number;
  cache?: CacheState;
  contracts?: string[];
  latest_contract?: string;
}

export interface ItemsFetch {
  items: Item[];
  todos: Todo[];
  fetched_at?: number;
  cache?: CacheState;
}

export interface OverlayFetch {
  tags: Record<string, string>;
}

export type CacheState = 'loading' | 'hit' | 'miss' | 'stale' | 'error' | 'unknown';

export interface WorklogState {
  events: WorklogEvent[];
  fetchedAt: number;
  cache: CacheState;
  error: string | null;
  contract: string | null;
  contracts: string[];
  latestContract: string | null;
}

export interface ItemsState {
  list: Item[];
  fetchedAt: number;
  cache: CacheState;
  error: string | null;
}

export interface TodosState {
  list: Todo[];
  fetchedAt: number;
  cache: CacheState;
  error: string | null;
}

export interface OverlayState {
  tags: Record<string, string>;
  contract: string | null;
  error: string | null;
}

export interface StoreShape {
  state: Record<string, boolean>;
  comments: Record<string, string>;
}

export interface TimeStats {
  closed_s: number;
  subtracted_s: number;
  active_s: number;
  total_s: number;
  has_active: boolean;
  is_paused: boolean;
  is_idle: boolean;
  active_task: string | null;
}

export type EventKind =
  | 'progress' | 'client' | 'consideration' | 'dashboard' | 'general'
  | 'link' | 'task' | 'session' | 'pause' | 'contract'
  | 'subtract' | 'correct' | 'status-change' | 'time-slice'
  | 'revise' | 'other';

export type TagSource = 'prefix' | 'overlay' | null;

export type TimeColMode = 'time' | 'both';
export type NavMode = 'category' | 'priority';
export type Theme = 'dark' | 'light';

export const ALL_EVENTS = '__all';

// Filter items down to the active contract. ALL_EVENTS sentinel disables
// the filter. Items with no contract appear only under ALL_EVENTS.
export function scopeItemsToContract<T extends { contract?: string | null }>(
  items: T[],
  activeContract: string,
): T[] {
  if (activeContract === ALL_EVENTS) return items;
  return items.filter(it => it.contract === activeContract);
}

// ---------------------------------------------------------------------------
// status.json snapshot shape - produced by the CLI's _build_status_json,
// consumed verbatim by the dashboard. Every visible number on the main
// dashboard derives from a field here; no client-side recomputation.
// ---------------------------------------------------------------------------

export interface SnapshotHeadline {
  today_s: number;
  calendar_week_s: number;
  contract_total_s: number;
  all_events_s: number;
}

export interface SnapshotCategoryProgress {
  done: number;
  total: number;
}

export interface SnapshotStatusCounts {
  active?: number;
  open?: number;
  blocked?: number;
  review?: number;
  done?: number;
  deferred?: number;
  partial?: number;
  continuing?: number;
  dropped?: number;
  total: number;
}

export interface SnapshotContract {
  name: string;
  from_epoch: number;
  to_epoch: number | null;
  active: boolean;
  // Per-item time worked WITHIN this contract (item_id -> seconds). Item ids
  // are reused across contracts for unrelated tasks, so the Tasks table scopes
  // each row's time to the selected contract via this map rather than the
  // all-time work_items.actual_seconds.
  item_seconds?: Record<string, number>;
  headline: SnapshotHeadline;
  status_counts: SnapshotStatusCounts;
  category_progress: Record<string, SnapshotCategoryProgress>;
}

export interface SnapshotActiveSession {
  task: string;
  item_id: string;
  session_n: number;
  session_start_epoch: number;
  block_open_epoch: number;
  is_paused: boolean;
  pause_start_epoch: number | null;
  total_paused_s: number;
  current_session_active_s: number;
  current_task_total_s: number;
}

export interface SnapshotItem {
  id: string;
  label: string;
  status: string;
  actual_seconds_derived: number;
  estimate_minutes: number | null;
  priority: number | null;
  contract: string | null;
  block_reason: string;
  created_at: string;
  completed_at: string | null;
  last_used_at: string | null;
}

export interface SnapshotTodo {
  id: number;
  name: string;
  details: string;
  resolved: number;
  created_at: string;
  resolved_at: string | null;
}

export interface Snapshot {
  published_at: number;
  published_at_local: string;
  cli_version: string;
  project_key: string;
  current_contract: string;
  selected_contract_default: string;
  contracts: SnapshotContract[];
  active_session: SnapshotActiveSession | null;
  items: SnapshotItem[];
  todos: SnapshotTodo[];
}
